import {useQuery} from '@tanstack/react-query';

import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';

import {wishlistKeys} from './keys';
import {useWishlist} from './useWishlist';

/**
 * Query hook for fetching resolved wishlist products
 *
 * Depends on useWishlist - waits for entries to be loaded, then
 * resolves product barcodes to full OrderLine product data via search.
 *
 * @returns Query result with array of OrderLine products
 *
 * @example
 * const {data: products, isLoading} = useWishlistProducts();
 * // products is OrderLine[] (or undefined while loading)
 */
export const useWishlistProducts = () => {
  const {data: wishlistData} = useWishlist();

  // Extract entry IDs to use as part of the query key
  // This ensures the query refetches when entries change
  const entryIds = wishlistData?.entries.map(e => e.id) || [];

  return useQuery<OrderLine[]>({
    queryKey: [...wishlistKeys.products(), entryIds],

    queryFn: async () => {
      if (!wishlistData?.entries.length) {
        return [];
      }

      const barcodes = wishlistData.entries.map(e => e.product_code);
      const barcodeSet = new Set(barcodes);
      const productIdSet = new Set(wishlistData.entries.map(e => e.product_id));

      const products = (await apiService.searchByBarcode(barcodes)) as unknown as OrderLine[];

      // Filter to only include products that are actually in the wishlist entries
      // (matches by product_code OR product_id to handle sync issues)
      return products.filter(
        product =>
          (product.product_code && barcodeSet.has(product.product_code)) ||
          (product.product_id && productIdSet.has(product.product_id)),
      );
    },

    // Only run when we have a wishlist with entries
    enabled: !!wishlistData?.entries.length,
  });
};
